import { useCallback, useEffect, useState } from "react";
import { useSyncProviders } from "./useSyncProviders";
import constantsValues from "../constants/constantsValues";

export const useMetaMask = () => {
  const [walletProvider, setWalletProvider] = useState(undefined);
  const [account, setAccount] = useState("");
  const [chainId, setChainId] = useState("");

  const providers = useSyncProviders();

  const connect = useCallback(
    async (chainToConnect) => {
      if (!walletProvider) return;

      if (chainId && chainToConnect !== chainId) {
        const switchNetworkResponse = await switchNetwork(chainToConnect);
        if (!switchNetworkResponse) return;
      }

      try {
        const accounts = await walletProvider.provider.request({
          method: "eth_requestAccounts",
        });

        if (accounts?.length) setAccount(accounts[0]);
        else setAccount("");
      } catch (error) {
        console.error("Error connecting to MetaMask:", error);
      }
    },
    [walletProvider]
  );

  const switchNetwork = useCallback(
    async (chainIdToConnect, manualProvider = undefined) => {
      const providerToUse = manualProvider ?? walletProvider?.provider;
      if (!providerToUse) return false;
      if (chainId === chainIdToConnect) return true;

      let chainToConnect = constantsValues[chainIdToConnect];

      if (!chainToConnect) {
        console.error("Unsupported network");
        return false;
      }

      try {
        await providerToUse.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: chainToConnect.chainID }],
        });

        return true;
      } catch (switchError) {
        if (switchError.code !== 4902) {
          console.error("Error switching network:", switchError);
          return false;
        }
        try {
          await providerToUse.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: chainToConnect.chainID,
                chainName: chainToConnect.ChainName,
                rpcUrls: [chainToConnect.rpcURL],
                nativeCurrency: {
                  name: chainToConnect.token.name,
                  symbol: chainToConnect.token.symbol,
                  decimals: chainToConnect.token.decimals,
                },
                blockExplorerUrls: [chainToConnect.blockExplorer],
              },
            ],
          });

          return true;
        } catch (addError) {
          console.error("Error adding network to metamask:", addError);
          return false;
        }
      }
    },
    [chainId, walletProvider]
  );

  useEffect(() => {
    const metamaskProvider = providers?.find((provider) => provider.info.name === "MetaMask");
    setWalletProvider(metamaskProvider);
  }, [providers]);

  useEffect(() => {
    const handleAccountsChanged = (accounts) => {
      if (accounts.length !== 0) {
        const currentAccount = accounts[0];
        setAccount(currentAccount);
      } else {
        setAccount("");
      }
    };

    if (walletProvider && !account) {
      connect();
      walletProvider.provider.on("accountsChanged", handleAccountsChanged);
    }
  }, [account, connect, walletProvider]);

  useEffect(() => {
    const detectChainId = async () => {
      if (!walletProvider) {
        setChainId("");
        return;
      }

      if (!account) {
        setChainId("");
        return;
      }

      try {
        const chainId = await walletProvider.provider.request({
          method: "eth_chainId",
        });
        setChainId(chainId);

        walletProvider.provider.on("chainChanged", (chainId) => {
          if (account) {
            setChainId(chainId);
          }
        });
      } catch (error) {
        console.error(error);
      }
    };

    detectChainId();
  }, [walletProvider, account]);

  return { walletProvider, account, chainId, connect, switchNetwork };
};
